<template>
  <header :class="['site-header', 'header-left-shifted', { 'is-scrolled': isScrolled }]">
    <div class="container site-header__inner">
      <router-link class="site-header__brand site-header__brand--offset" to="/">
        <img src="/images/logo.png" alt="North Horizon logo">
      </router-link>
      <nav class="site-header__nav">
        <router-link v-for="item in localizedNavItems" :key="item.key" :to="item.to">{{ item.label }}</router-link>
      </nav>
      <div class="site-header__controls">
        <div class="site-header__lang-toggle" role="group" aria-label="Language switcher">
          <button data-test="lang-en" type="button" :class="{ 'is-active': currentLanguage === 'en' }" @click="setLanguage('en')">EN</button>
          <button data-test="lang-ja" type="button" :class="{ 'is-active': currentLanguage === 'ja' }" @click="setLanguage('ja')">JP</button>
        </div>
        <button class="site-header__menu-btn" type="button" @click="toggleMenu" aria-label="Toggle navigation menu">
          <span></span>
          <span></span>
        </button>
      </div>
    </div>
    <transition name="menu-fade">
      <div v-if="menuOpen" class="site-header__mobile">
        <div class="site-header__mobile-lang-toggle" role="group" aria-label="Mobile language switcher">
          <button data-test="mobile-lang-en" type="button" :class="{ 'is-active': currentLanguage === 'en' }" @click="setLanguage('en')">EN</button>
          <button data-test="mobile-lang-ja" type="button" :class="{ 'is-active': currentLanguage === 'ja' }" @click="setLanguage('ja')">JP</button>
        </div>
        <router-link v-for="item in localizedNavItems" :key="item.key" :to="item.to" @click.native="closeMenu">{{ item.label }}</router-link>
      </div>
    </transition>
  </header>
</template>

<script>
var navItems = [
  {
    key: 'home',
    to: '/',
    labels: {
      en: 'HOME',
      ja: 'ホーム'
    }
  },
  {
    key: 'company',
    to: '/company',
    labels: {
      en: 'COMPANY',
      ja: '会社概要'
    }
  },
  {
    key: 'services',
    to: '/services',
    labels: {
      en: 'SERVICES',
      ja: '事業内容'
    }
  },
  {
    key: 'why-us',
    to: '/why-us',
    labels: {
      en: 'WHY US',
      ja: '当社の強み'
    }
  },
  {
    key: 'portfolio',
    to: '/portfolio',
    labels: {
      en: 'PORTFOLIO',
      ja: '実績案件'
    }
  },
  {
    key: 'news',
    to: '/news',
    labels: {
      en: 'NEWS',
      ja: '新着情報'
    }
  },
  {
    key: 'contact',
    to: '/contact',
    labels: {
      en: 'CONTACT',
      ja: 'お問い合わせ'
    }
  }
];

export default {
  name: 'SiteHeader',
  props: {
    language: {
      type: String,
      default: 'en'
    }
  },
  data: function () {
    return {
      isScrolled: false,
      menuOpen: false,
      navItems: navItems
    };
  },
  computed: {
    currentLanguage: function () {
      return this.language;
    },
    localizedNavItems: function () {
      var language = this.currentLanguage;

      return this.navItems.map(function (item) {
        return {
          key: item.key,
          to: item.to,
          label: item.labels[language]
        };
      });
    }
  },
  mounted: function () {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
  },
  beforeDestroy: function () {
    window.removeEventListener('scroll', this.handleScroll);
  },
  watch: {
    $route: function () {
      this.closeMenu();
    }
  },
  methods: {
    handleScroll: function () {
      this.isScrolled = window.scrollY > 24;
    },
    toggleMenu: function () {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu: function () {
      this.menuOpen = false;
    },
    setLanguage: function (language) {
      this.$emit('change-language', language);
    }
  }
};
</script>

<style scoped>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 30;
  padding: 28px 0 28px 0;
  background: rgba(255, 251, 246, 0.26);
  box-shadow: 0 6px 18px rgba(60, 45, 34, 0.035);
  backdrop-filter: blur(10px);
  transition: background-color 0.35s ease, box-shadow 0.35s ease, padding 0.35s ease;
}

.site-header.is-scrolled {
  background: rgba(255, 251, 246, 0.94);
  box-shadow: 0 14px 32px rgba(60, 45, 34, 0.08);
  backdrop-filter: blur(14px);
  padding: 14px 0 14px 0;
}

.site-header__inner {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: nowrap;
}

.site-header__brand {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 32px;
}

.site-header__brand--offset {
  margin-left: -72px;
}

.site-header__brand img {
  display: block;
  width: auto;
  height: 56px;
}

.site-header__nav {
  display: flex;
  gap: 39px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.056em;
  text-transform: uppercase;
  flex: 1;
  justify-content: center;
  margin-left: 0;
}

.site-header__controls {
  display: flex;
  align-items: flex-end;
  gap: 18px;
  flex-shrink: 0;
}

.site-header__lang-toggle,
.site-header__mobile-lang-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border: 1px solid rgba(109, 86, 68, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
}

.site-header__lang-toggle button,
.site-header__mobile-lang-toggle button {
  min-width: 38px;
  padding: 7px 12px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--color-muted);
}

.site-header__lang-toggle button.is-active,
.site-header__mobile-lang-toggle button.is-active {
  background: var(--color-accent-deep);
  color: #fff;
}

.site-header__nav a,
.site-header__mobile a {
  position: relative;
  padding-bottom: 8px;
  white-space: nowrap;
}

.site-header__nav a::after,
.site-header__mobile a::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 1px;
  background: var(--color-accent-deep);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.35s ease;
}

.site-header__nav a:hover::after,
.site-header__nav a.router-link-exact-active::after,
.site-header__mobile a:hover::after,
.site-header__mobile a.router-link-exact-active::after {
  transform: scaleX(1);
}

.site-header__menu-btn {
  display: none;
  width: 48px;
  height: 48px;
  border: 1px solid rgba(109, 86, 68, 0.18);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.72);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
}

.site-header__menu-btn span {
  width: 18px;
  height: 1.5px;
  background: var(--color-text);
}

.site-header__mobile {
  margin: 12px auto 0;
  width: min(calc(100% - 28px), 420px);
  padding: 24px;
  border: 1px solid var(--color-line);
  border-radius: 24px;
  background: rgba(255, 252, 247, 0.98);
  box-shadow: var(--shadow-soft);
  display: grid;
  gap: 18px;
}

.site-header__mobile-lang-toggle {
  justify-self: start;
}

.site-header__mobile-lang-toggle button {
  min-width: 38px;
  padding: 7px 12px;
  font-size: 11px;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.menu-fade-enter,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@media (max-width: 1439px) {
  .site-header {
    padding: 26px 0 26px 0;
  }

  .site-header.is-scrolled {
    padding: 13px 0 13px 0;
  }

  .site-header__inner {
    gap: 11px;
  }

  .site-header__brand {
    margin-right: 38px;
  }

  .site-header__brand--offset {
    margin-left: -36px;
  }

  .site-header__brand img {
    height: 66px;
  }

  .site-header__nav {
    gap: 30px;
    font-size: 16px;
    letter-spacing: 0.05em;
  }

  .site-header__controls {
    gap: 14px;
  }

  .site-header__lang-toggle {
    padding: 5px;
  }

  .site-header__lang-toggle button {
    min-width: 34px;
    padding: 6px 10px;
    font-size: 10px;
  }
}

@media (max-width: 1279px) {
  .site-header {
    padding: 24px 0 24px 0;
  }

  .site-header.is-scrolled {
    padding: 12px 0 12px 0;
  }

  .site-header__inner {
    gap: 10px;
  }

  .site-header__brand {
    margin-right: 26px;
  }

  .site-header__brand--offset {
    margin-left: -24px;
  }

  .site-header__brand img {
    height: 58px;
  }

  .site-header__nav {
    gap: 23px;
    font-size: 15px;
    letter-spacing: 0.03em;
  }

  .site-header__controls {
    gap: 12px;
  }

  .site-header__lang-toggle {
    gap: 4px;
    padding: 4px;
  }

  .site-header__lang-toggle button {
    min-width: 30px;
    padding: 5px 8px;
    font-size: 9px;
  }
}

@media (max-width: 1151px) {
  .site-header {
    padding: 20px 0 20px 0;
  }

  .site-header.is-scrolled {
    padding: 10px 0 10px 0;
  }

  .site-header__inner {
    gap: 8px;
  }

  .site-header__brand {
    margin-right: 14px;
  }

  .site-header__brand--offset {
    margin-left: -12px;
  }

  .site-header__brand img {
    height: 50px;
  }

  .site-header__nav {
    gap: 15px;
    font-size: 14px;
    letter-spacing: 0.02em;
  }

  .site-header__controls {
    gap: 8px;
  }

  .site-header__lang-toggle {
    gap: 3px;
    padding: 3px;
  }

  .site-header__lang-toggle button {
    min-width: 28px;
    padding: 4px 6px;
    font-size: 8px;
  }
}

@media (max-width: 767px) {
  .site-header__nav,
  .site-header__lang-toggle {
    display: none;
  }

  .site-header__menu-btn {
    display: inline-flex;
  }

  .site-header__brand img {
    height: 40px;
  }
}
</style>

